import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ThemeProvider } from '@/lib/theme';
import { Toaster } from '@/components/ui/toast';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuthStore } from '@/lib/auth';
import { useAuthBootstrap } from '@/hooks/useAuth';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { ShieldAlert } from 'lucide-react';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const InventoryPage = lazy(() => import('@/pages/InventoryPage'));
const KeyDetailPage = lazy(() => import('@/pages/KeyDetailPage'));
const KeyFormPage = lazy(() => import('@/pages/KeyFormPage'));
const SalesPage = lazy(() => import('@/pages/SalesPage'));
const CompatibilityPage = lazy(() => import('@/pages/CompatibilityPage'));
const LabelPrintPage = lazy(() => import('@/pages/LabelPrintPage'));
const BoxLabelPage = lazy(() => import('@/pages/BoxLabelPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

function PageFallback() {
  return (
    <div className="flex h-64 items-center justify-center">
      <LoadingSpinner center label={t('action.loading')} />
    </div>
  );
}

function ProtectedRoute() {
  const { user, bootstrapping } = useAuthStore();
  const location = useLocation();

  if (bootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner label={t('action.loading')} />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return (
    <AppShell>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border bg-card p-12 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <ShieldAlert className="h-6 w-6 text-destructive" />
        </div>
        <h2 className="mb-1 text-lg font-semibold">{t('settings.forbiddenTitle')}</h2>
        <p className="mb-4 text-sm text-muted-foreground">{t('settings.forbiddenDesc')}</p>
        <Button asChild variant="outline">
          <a href="/">{t('common.goHome')}</a>
        </Button>
      </div>
    );
  }
  return <>{children}</>;
}

function AppBootstrap({ children }: { children: ReactNode }) {
  useAuthBootstrap();
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <BrowserRouter>
              <AppBootstrap>
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route element={<ProtectedRoute />}>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/keys" element={<InventoryPage />} />
                      <Route path="/keys/new" element={<KeyFormPage />} />
                      <Route path="/keys/:id" element={<KeyDetailPage />} />
                      <Route path="/keys/:id/edit" element={<KeyFormPage />} />
                      <Route path="/sales" element={<SalesPage />} />
                      <Route path="/compat" element={<CompatibilityPage />} />
                      <Route path="/labels" element={<LabelPrintPage />} />
                      <Route path="/labels/box" element={<BoxLabelPage />} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route
                        path="/settings"
                        element={
                          <AdminOnly>
                            <SettingsPage />
                          </AdminOnly>
                        }
                      />
                    </Route>
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </AppBootstrap>
            </BrowserRouter>
            <Toaster />
          </TooltipProvider>
          {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
